import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Container, Row, Col, Alert, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [currentUserData, setCurrentUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Fonction pour charger les données complètes de l'utilisateur connecté
  const loadCurrentUserData = useCallback(async () => {
    // Attendre que l'authentification soit chargée
    if (authLoading) {
      return;
    }

    // Si l'utilisateur n'est pas connecté après le chargement, arrêter
    if (!user?.email) {
      setIsLoading(false);
      setIsAuthorized(false);
      return;
    }
    
    try {
      const { getUserProfile, getProviderProfile } = await import('../firebase/firebaseFirestore');
      let result: { success: boolean; data?: any };
      if (user.role === 'Prestataire') {
        result = await getProviderProfile(user.email);
      } else {
        result = await getUserProfile(user.email);
      }
      if (result.success && result.data) {
        console.log('🔍 [ProtectedAdminRoute] Données complètes de l\'utilisateur connecté:', result.data);
        setCurrentUserData(result.data);
        
        // Privilèges admin : rôle Administrateur (legacy) ou isAdmin
        const isAdmin = result.data.role === 'Administrateur' || result.data.isAdmin === true;
        const finalAuthorization = isAdmin || user?.isAdmin === true;
        
        console.log('🔍 [ProtectedAdminRoute] Vérification des permissions:');
        console.log('📧 Email:', user.email);
        console.log('👑 Rôle:', result.data.role);
        console.log('🔑 isAdmin:', result.data.isAdmin);
        console.log('✅ Est admin:', finalAuthorization);
        
        setIsAuthorized(finalAuthorization);
        
        if (!finalAuthorization) {
          console.log('🚫 [ProtectedAdminRoute] Utilisateur non autorisé:', result.data);
        } else {
          console.log('✅ [ProtectedAdminRoute] Utilisateur autorisé:', result.data);
        }
      } else {
        // Fallback : utiliser user.isAdmin du contexte (données de connexion)
        const fallbackAdmin = user?.isAdmin === true || user?.role === 'Administrateur';
        setIsAuthorized(!!fallbackAdmin);
        if (!fallbackAdmin) {
          console.log('❌ [ProtectedAdminRoute] Impossible de charger les données utilisateur');
        }
      }
    } catch (error) {
      console.error('❌ [ProtectedAdminRoute] Erreur lors du chargement des données utilisateur:', error);
      setIsAuthorized(false);
    } finally {
      setIsLoading(false);
    }
  }, [user, authLoading]);

  // Charger les données utilisateur au montage du composant et quand user/authLoading change
  useEffect(() => {
    loadCurrentUserData();
  }, [loadCurrentUserData]);

  // Si l'authentification est en cours de chargement, afficher un spinner
  if (authLoading || isLoading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p>Vérification des permissions...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Si l'utilisateur n'est pas connecté après le chargement, rediriger vers la page d'accueil
  if (!user) {
    console.log('🚫 [ProtectedAdminRoute] Utilisateur non connecté, redirection vers l\'accueil');
    return <Navigate to="/" replace />;
  }

  // Si l'utilisateur n'est pas autorisé (pas administrateur), afficher un message d'erreur
  if (!isAuthorized) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Alert variant="danger" className="text-center">
              <i className="bi bi-shield-exclamation me-2"></i>
              <strong>Accès refusé</strong><br />
              Seuls les administrateurs peuvent accéder à cette page.
            </Alert>
            <Alert variant="info" className="mt-3">
              <strong>Debug Info:</strong><br />
              Email: {user?.email}<br />
              Rôle: {currentUserData?.role || 'Non défini'}<br />
              Données complètes: {JSON.stringify(currentUserData, null, 2)}
            </Alert>
            <div className="text-center mt-3">
              <Button variant="primary" onClick={() => window.location.href = '/'}>
                <i className="bi bi-house me-2"></i>
                Retour à l'accueil
              </Button>
              <Button 
                variant="danger" 
                className="ms-2"
                onClick={async () => {
                  console.log('🚨 RESTAURATION D\'URGENCE DU RÔLE ADMINISTRATEUR');
                  const { saveUserProfile } = await import('../firebase/firebaseFirestore');
                  const result = await saveUserProfile(user.email, {
                    email: user.email,
                    role: 'Administrateur',
                    name: 'EL ORCHE',
                    firstName: 'Zakaria',
                    lastName: 'EL ORCHE',
                    updatedAt: new Date().toISOString(),
                  });
                  if (result.success) {
                    alert('✅ RÔLE ADMINISTRATEUR RESTAURÉ! La page va se recharger...');
                    window.location.reload();
                  } else {
                    alert('❌ Erreur: ' + result.error);
                  }
                }}
              >
                🚨 RESTAURER MON RÔLE
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Si l'utilisateur est autorisé, afficher le contenu
  return <>{children}</>;
};

export default ProtectedAdminRoute;